import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useLocation } from "react-router";

import { useDebouncedCallback } from "./use-debounced-callback";

const DEBOUNCE_SAVE_DELAY_MS = 1000;

const autosaveContext = createContext();

export function useAutosaveContext() {
  return useContext(autosaveContext);
}

function useProvideAutosave() {
  const [isSaved, setIsSaved] = useState(true);
  const [isFormErrors, setIsFormErrors] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const debouncedSaveRef = useRef(null);

  const cancelDebounce = () => {
    if (debouncedSaveRef.current) {
      debouncedSaveRef.current.cancel();
    }
    setIsSaved(true);
  };

  return {
    isSaved,
    setIsSaved,
    isFormErrors,
    setIsFormErrors,
    isStreaming,
    setIsStreaming,
    cancelDebounce,
    debouncedSaveRef,
  };
}

export function AutosaveProvider({ children }) {
  const autosave = useProvideAutosave();

  return (
    <autosaveContext.Provider value={autosave}>
      {children}
    </autosaveContext.Provider>
  );
}

export function useAutosave({ save, getValues, control }) {
  const {
    setIsSaved,
    setIsFormErrors,
    isStreaming,
    debouncedSaveRef,
  } = useAutosaveContext();

  const location = useLocation();
  const isScribe = location?.pathname?.includes("scribetool");

  // Watch the entire form — triggers a re-render on any field change.
  // This is the hook-based equivalent of the old watch() subscription.
  const formValues = useWatch({ control });
  const { isDirty } = useFormState({ control });

  const debouncedSave = useDebouncedCallback(async () => {
    const data = getValues();
    const result = await save({
      data,
      isDraft: true,
      isAutosave: true,
    });
    if (result === "form-errors") {
      setIsFormErrors(true);
    } else {
      setIsSaved(true);
      setIsFormErrors(false);
    }
  }, DEBOUNCE_SAVE_DELAY_MS);

  useEffect(() => {
    debouncedSaveRef.current = debouncedSave;
  }, [debouncedSave, debouncedSaveRef]);

  useEffect(() => {
    if (isScribe || isStreaming || !isDirty) return;

    setIsSaved(false);
    debouncedSave();
  }, [
    formValues,
    isScribe,
    isStreaming,
    isDirty,
    setIsSaved,
    debouncedSave,
  ]);
}
